/**
 * DateRangePicker - A pair of native date inputs that lets the user
 * pick an inclusive [from, to] window.
 *
 * Native <input type="date"> is used intentionally:
 *   - Mobile-first: tapping the input opens the platform's native
 *     date picker (iOS scroll wheels, Android Material picker), which
 *     is faster and more accessible than any custom calendar widget.
 *   - Zero JS overhead and no dependency on a calendar library.
 *
 * The component is fully controlled — the parent owns the from/to
 * strings (ISO YYYY-MM-DD format) and receives change events via
 * `onChange({ from, to })`.
 *
 * Validation rules:
 *   - Empty values are allowed (treated as "no bound").
 *   - When both values are set, the component clamps `to` to be no
 *     earlier than `from` by passing the appropriate `min` attribute
 *     on the `to` input.
 *
 * Used by the Calendar and Map filter bars to pick the visible window.
 */
import React from 'react';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/**
 * The date range value. Either bound may be empty (`''`) which the
 * parent should treat as "unbounded on this side".
 */
export interface DateRangeValue {
    from: string;
    to: string;
}

/**
 * Props for DateRangePicker.
 *
 * @property value      Current range; controlled by the parent
 * @property onChange   Called with the next range on either input change
 * @property labelFrom  Label text for the start input (default: "From")
 * @property labelTo    Label text for the end input (default: "To")
 * @property className  Optional extra classes for the outer wrapper
 * @property disabled   When true, both inputs are disabled
 */
export interface DateRangePickerProps {
    value: DateRangeValue;
    onChange: (next: DateRangeValue) => void;
    labelFrom?: string;
    labelTo?: string;
    className?: string;
    disabled?: boolean;
}

/* ============================================
 * COMPONENT
 * ============================================ */

const inputClass = 'w-full px-12px py-10px min-h-44px desktop:min-h-36px border border-brand-border rounded-md text-16px text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary bg-white';
const labelClass = 'block text-12px font-sans font-medium text-brand-text-muted uppercase tracking-wide mb-2px';

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
    value,
    onChange,
    labelFrom = 'From',
    labelTo = 'To',
    className,
    disabled = false,
}) => {
    /** Build the next range with `from` updated, preserving `to`. */
    const handleFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange({ from: e.target.value, to: value.to });
    };

    /** Build the next range with `to` updated, preserving `from`. */
    const handleToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange({ from: value.from, to: e.target.value });
    };

    return (
        <div className={['date-range-picker flex flex-col tablet:flex-row tablet:items-end gap-8px', className ?? ''].join(' ')} data-ref="molecule-date-range-picker">
            <div className="date-range-picker__field w-full tablet:w-auto">
                <label htmlFor="date-range-from" className={labelClass}>{labelFrom}</label>
                <input
                    id="date-range-from"
                    type="date"
                    value={value.from}
                    onChange={handleFromChange}
                    disabled={disabled}
                    className={inputClass}
                />
            </div>
            <div className="date-range-picker__field w-full tablet:w-auto">
                <label htmlFor="date-range-to" className={labelClass}>{labelTo}</label>
                <input
                    id="date-range-to"
                    type="date"
                    value={value.to}
                    onChange={handleToChange}
                    /* Prevent picking an end date before the start date.
                     * The native picker greys out earlier dates automatically. */
                    min={value.from || undefined}
                    disabled={disabled}
                    className={inputClass}
                />
            </div>
        </div>
    );
};

export default DateRangePicker;
